import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronDown } from 'lucide-react';
import PropTypes from 'prop-types';
import useDropdownPosition from '../../../hooks/useDropdownPosition';
import { RACES } from '../../../constants/races';
import { GENDERS } from '../../../constants/genders';

const DefendantsDropdown = ({
  isOpen,
  onClose,
  selectedDefendants = [],
  onDefendantsChange,
  demographics,
  onDemographicsChange,
  buttonRef
}) => {
  const [defendantEntry, setDefendantEntry] = useState({
    firstName: '',
    middleName: '',
    lastName: ''
  });

  const [demographicFilters, setDemographicFilters] = useState({
    race: demographics?.race || null,
    gender: demographics?.gender || null,
    minAge: demographics?.minAge || null,
    maxAge: demographics?.maxAge || null
  });

  // Sync internal state with demographics prop when it changes
  useEffect(() => {
    setDemographicFilters({
      race: demographics?.race || null,
      gender: demographics?.gender || null,
      minAge: demographics?.minAge || null,
      maxAge: demographics?.maxAge || null
    });
  }, [demographics]);

  // Use smart positioning hook
  const { dropdownRef, position } = useDropdownPosition(isOpen, 320, buttonRef);

  // Check if we're on a narrow screen (mobile)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Check if the click is on a filter button
        const isFilterButton = event.target.closest('[data-filter-button]');
        if (isFilterButton) {
          return;
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, dropdownRef]);

  // Handle defendant search submission
  const handleDefendantSubmit = (e) => {
    e.preventDefault();
    if (
      defendantEntry.firstName ||
      defendantEntry.middleName ||
      defendantEntry.lastName
    ) {
      // Concatenate first and middle names for backend
      const defendantForBackend = {
        firstName:
          `${defendantEntry.firstName} ${defendantEntry.middleName}`.trim(),
        lastName: defendantEntry.lastName
      };
      // Set the defendant for search (replace any existing defendant)
      onDefendantsChange([defendantForBackend]);
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    onDefendantsChange([]);
    setDemographicFilters({
      race: null,
      gender: null,
      minAge: null,
      maxAge: null
    });
    if (onDemographicsChange) {
      onDemographicsChange({
        race: null,
        gender: null,
        minAge: null,
        maxAge: null
      });
    }
  };

  // Debounce timer ref for age filter changes
  const ageFilterDebounceRef = useRef(null);
  const demographicFiltersRef = useRef(demographicFilters);

  // Keep ref in sync with state
  useEffect(() => {
    demographicFiltersRef.current = demographicFilters;
  }, [demographicFilters]);

  // Handle demographic filter changes (immediate for non-age fields, debounced for age)
  const handleDemographicChange = useCallback(
    (field, value) => {
      const updated = { ...demographicFiltersRef.current, [field]: value };
      setDemographicFilters(updated);
      demographicFiltersRef.current = updated; // Update ref immediately

      // Age filters are debounced to avoid API overload
      if (field === 'minAge' || field === 'maxAge') {
        // Clear existing timeout
        if (ageFilterDebounceRef.current) {
          clearTimeout(ageFilterDebounceRef.current);
        }

        // Set new timeout - capture the updated value
        ageFilterDebounceRef.current = setTimeout(() => {
          if (onDemographicsChange) {
            // Use the latest state from ref (which was updated above)
            onDemographicsChange(demographicFiltersRef.current);
          }
          ageFilterDebounceRef.current = null;
        }, 500); // 500ms debounce delay
      } else {
        // Non-age fields update immediately
        if (onDemographicsChange) {
          onDemographicsChange(updated);
        }
      }
    },
    [onDemographicsChange]
  );

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (ageFilterDebounceRef.current) {
        clearTimeout(ageFilterDebounceRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && (
        <div
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={onClose}
        />
      )}

      <div
        ref={dropdownRef}
        className={`${
          isMobile
            ? 'fixed inset-x-4 top-1/2 -translate-y-1/2 w-auto max-w-sm mx-auto'
            : 'absolute top-full mt-1 w-80 sm:w-80'
        } bg-white border border-gray-200 rounded-lg shadow-lg z-50`}
        style={
          isMobile
            ? {
                maxWidth: 'calc(100vw - 32px)',
                minWidth: '280px'
              }
            : {
                left: position.left,
                right: position.right,
                maxWidth: 'calc(100vw - 32px)', // Ensure dropdown never exceeds viewport width
                minWidth: '320px' // Ensure minimum usable width
              }
        }
      >
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-900">
              Select Defendants
            </h3>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Defendant Search Form */}
          <div className="mb-4">
            <div className="text-xs font-medium text-gray-700 mb-2">
              Search by Defendant Name
            </div>
            <form onSubmit={handleDefendantSubmit} className="space-y-2">
              <div className="relative">
                <input
                  type="text"
                  placeholder="First Names"
                  value={defendantEntry.firstName}
                  onChange={(e) =>
                    setDefendantEntry((prev) => ({
                      ...prev,
                      firstName: e.target.value
                    }))
                  }
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {defendantEntry.firstName && (
                  <button
                    type="button"
                    onClick={() =>
                      setDefendantEntry((prev) => ({
                        ...prev,
                        firstName: ''
                      }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Middle Name"
                  value={defendantEntry.middleName}
                  onChange={(e) =>
                    setDefendantEntry((prev) => ({
                      ...prev,
                      middleName: e.target.value
                    }))
                  }
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {defendantEntry.middleName && (
                  <button
                    type="button"
                    onClick={() =>
                      setDefendantEntry((prev) => ({
                        ...prev,
                        middleName: ''
                      }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Last Name"
                  value={defendantEntry.lastName}
                  onChange={(e) =>
                    setDefendantEntry((prev) => ({
                      ...prev,
                      lastName: e.target.value
                    }))
                  }
                  className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {defendantEntry.lastName && (
                  <button
                    type="button"
                    onClick={() =>
                      setDefendantEntry((prev) => ({
                        ...prev,
                        lastName: ''
                      }))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="w-full px-3 py-2 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 text-sm font-medium"
              >
                Search
              </button>
            </form>
          </div>

          {/* Demographic Filters */}
          <div className="mb-4 border-t border-gray-200 pt-4">
            <div className="text-xs font-medium text-gray-700 mb-3">
              Filter by Demographics
            </div>
            <div className="space-y-3">
              {/* Race Filter */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">Race</label>
                <div className="relative">
                  <select
                    value={demographicFilters.race || ''}
                    onChange={(e) =>
                      handleDemographicChange('race', e.target.value || null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none pr-8"
                  >
                    <option value="">Any</option>
                    {RACES.map((race) => (
                      <option key={race} value={race}>
                        {race}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Gender Filter */}
              <div>
                <label className="block text-xs text-gray-600 mb-1">
                  Gender
                </label>
                <div className="relative">
                  <select
                    value={demographicFilters.gender || ''}
                    onChange={(e) =>
                      handleDemographicChange('gender', e.target.value || null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none pr-8"
                  >
                    <option value="">Any</option>
                    {GENDERS.map((gender) => (
                      <option key={gender} value={gender}>
                        {gender}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Age Range Filter - Double Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs text-gray-600">
                    Age Range
                  </label>
                  <span className="text-xs font-medium text-gray-700">
                    {demographicFilters.minAge ?? 0} -{' '}
                    {demographicFilters.maxAge ?? 120} years
                  </span>
                </div>
                <div className="relative h-6 flex items-center">
                  {/* Track background */}
                  <div className="absolute w-full h-2 bg-gray-200 rounded-full" />
                  {/* Active range fill */}
                  <div
                    className="absolute h-2 bg-blue-500 rounded-full transition-all duration-150"
                    style={{
                      left: `${((demographicFilters.minAge ?? 0) / 120) * 100}%`,
                      width: `${(((demographicFilters.maxAge ?? 120) - (demographicFilters.minAge ?? 0)) / 120) * 100}%`
                    }}
                  />
                  {/* Min slider */}
                  <input
                    type="range"
                    min="0"
                    max="120"
                    value={demographicFilters.minAge ?? 0}
                    onChange={(e) => {
                      const newMin = parseInt(e.target.value);
                      const currentMax = demographicFilters.maxAge ?? 120;
                      if (newMin <= currentMax) {
                        handleDemographicChange('minAge', newMin);
                      }
                    }}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer range-slider range-slider-min"
                    style={{
                      zIndex:
                        (demographicFilters.minAge ?? 0) >
                        (demographicFilters.maxAge ?? 120) - 10
                          ? 5
                          : 3
                    }}
                  />
                  {/* Max slider */}
                  <input
                    type="range"
                    min="0"
                    max="120"
                    value={demographicFilters.maxAge ?? 120}
                    onChange={(e) => {
                      const newMax = parseInt(e.target.value);
                      const currentMin = demographicFilters.minAge ?? 0;
                      if (newMax >= currentMin) {
                        handleDemographicChange('maxAge', newMax);
                      }
                    }}
                    className="absolute w-full h-2 bg-transparent appearance-none cursor-pointer range-slider range-slider-max"
                    style={{
                      zIndex:
                        (demographicFilters.maxAge ?? 120) <
                        (demographicFilters.minAge ?? 0) + 10
                          ? 5
                          : 4
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Current Search */}
          {(selectedDefendants.length > 0 ||
            demographicFilters.race ||
            demographicFilters.gender ||
            demographicFilters.minAge ||
            demographicFilters.maxAge) && (
            <div className="mb-4">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Active Filters
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedDefendants.map((defendant, index) => (
                  <span
                    key={`${defendant.firstName}-${defendant.lastName}-${index}`}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {defendant.firstName} {defendant.lastName}
                    <button
                      onClick={() => onDefendantsChange([])}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {demographicFilters.race && (
                  <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                    Race: {demographicFilters.race}
                    <button
                      onClick={() => handleDemographicChange('race', null)}
                      className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {demographicFilters.gender && (
                  <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                    Gender: {demographicFilters.gender}
                    <button
                      onClick={() => handleDemographicChange('gender', null)}
                      className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {(demographicFilters.minAge || demographicFilters.maxAge) && (
                  <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                    Age: {demographicFilters.minAge || '0'}-
                    {demographicFilters.maxAge || '120'}
                    <button
                      onClick={() => {
                        // Clear both age filters in a single update
                        const updated = {
                          ...demographicFilters,
                          minAge: null,
                          maxAge: null
                        };
                        setDemographicFilters(updated);
                        if (onDemographicsChange) {
                          onDemographicsChange(updated);
                        }
                      }}
                      className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 mt-4">
            <button
              onClick={handleClearAll}
              className="text-sm text-gray-500 hover:text-gray-700"
              disabled={
                selectedDefendants.length === 0 &&
                !demographicFilters.race &&
                !demographicFilters.gender &&
                !demographicFilters.minAge &&
                !demographicFilters.maxAge
              }
            >
              Clear All
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

DefendantsDropdown.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDefendants: PropTypes.arrayOf(
    PropTypes.shape({
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired
    })
  ),
  onDefendantsChange: PropTypes.func.isRequired,
  demographics: PropTypes.shape({
    race: PropTypes.string,
    gender: PropTypes.string,
    minAge: PropTypes.number,
    maxAge: PropTypes.number
  }),
  onDemographicsChange: PropTypes.func,
  buttonRef: PropTypes.object
};

export default DefendantsDropdown;
